/**
 * @file
 * Status Messages with hover pause/resume functionality.
 */

(function (Drupal, once) {
  'use strict';

  const AUTO_DISMISS_DURATION = 7_000;
  const FADE_OUT_DURATION = 700;
  const COLLAPSE_DURATION = 200;

  class StatusMessageController {
    constructor(element) {
      this.element = element;
      this.progressBar = null;
      this.autoDismissTimer = null;
      this.isDismissed = false;
      this.startTime = null;
      this.remainingTime = AUTO_DISMISS_DURATION;
      this.init();
    }

    init() {
      this.createProgressBar();
      this.setupCloseButton();
      this.setupHoverHandlers();
      this.startAutoDismiss();
    }

    createProgressBar() {
      this.progressBar = document.createElement('div');
      this.progressBar.className = 'status-message__progress-bar';
      this.element.appendChild(this.progressBar);
    }

    setupCloseButton() {
      const closeButton = this.element.querySelector('[data-dismiss="status-message"]');
      if (closeButton) {
        closeButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.dismiss();
        });
      }
    }

    setupHoverHandlers() {
      this.element.addEventListener('mouseenter', () => this.pause());
      this.element.addEventListener('mouseleave', () => this.resume());
    }

    startAutoDismiss() {
      if (this.isDismissed || !this.progressBar) return;

      this.startTime = Date.now();

      // Set CSS custom property for the full duration
      this.element.style.setProperty('--progress-duration', `${this.remainingTime}ms`);

      // Start progress bar at 100% width
      this.progressBar.style.width = '100%';
      this.progressBar.offsetWidth; // Force reflow

      // Start the progress animation
      this.element.classList.add('progress-running');
      this.element.classList.remove('progress-paused');

      // Animate to 0% width (shrinking from right to left)
      this.progressBar.style.width = '0%';

      // Set timeout for auto-dismiss
      this.autoDismissTimer = setTimeout(() => {
        if (!this.isDismissed) this.dismiss();
      }, this.remainingTime);
    }

    pause() {
      if (this.isDismissed || !this.progressBar || !this.startTime) return;

      // Clear the auto-dismiss timer
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }

      // Calculate how much time has elapsed
      const elapsed = Date.now() - this.startTime;
      this.remainingTime = Math.max(0, this.remainingTime - elapsed);

      // Get current width percentage
      const computedStyle = getComputedStyle(this.progressBar);
      const currentWidth = parseFloat(computedStyle.width);
      const containerWidth = this.progressBar.parentElement.offsetWidth;
      const currentPercentage = (currentWidth / containerWidth) * 100;

      // Pause the animation
      this.element.classList.remove('progress-running');
      this.element.classList.add('progress-paused');

      // Fix the width at current position
      this.progressBar.style.width = `${currentPercentage}%`;
    }

    resume() {
      if (this.isDismissed || !this.progressBar) return;

      // Don't resume if already past the dismiss time
      if (this.remainingTime <= 0) {
        this.dismiss();
        return;
      }

      // Get current width percentage
      const computedStyle = getComputedStyle(this.progressBar);
      const currentWidth = parseFloat(computedStyle.width);
      const containerWidth = this.progressBar.parentElement.offsetWidth;
      const currentPercentage = (currentWidth / containerWidth) * 100;

      // Don't resume if progress is already complete
      if (currentPercentage <= 0) {
        this.dismiss();
        return;
      }

      // Update start time for next pause calculation
      this.startTime = Date.now();

      // Update CSS duration to remaining time
      this.element.style.setProperty('--progress-duration', `${this.remainingTime}ms`);

      // Resume animation
      this.element.classList.remove('progress-paused');
      this.element.classList.add('progress-running');

      // Force reflow to restart transition
      this.progressBar.offsetWidth;

      // Continue animating to 0%
      this.progressBar.style.width = '0%';

      // Set new timer for remaining duration
      this.autoDismissTimer = setTimeout(() => {
        if (!this.isDismissed) this.dismiss();
      }, this.remainingTime);
    }

    dismiss() {
      if (this.isDismissed) return;
      this.isDismissed = true;

      // Clear any timers
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }

      // Remove animation classes
      this.element.classList.remove('progress-running', 'progress-paused');
      if (this.progressBar) {
        this.progressBar.style.display = 'none';
      }

      // Start fade out
      this.element.classList.add('fade-out');
      setTimeout(() => this.collapse(), FADE_OUT_DURATION);
    }

    collapse() {
      const el = this.element;
      const styles = getComputedStyle(el);

      // Store current dimensions
      el.style.height = `${el.offsetHeight}px`;
      el.style.paddingTop = styles.paddingTop;
      el.style.paddingBottom = styles.paddingBottom;
      el.style.marginBottom = styles.marginBottom;
      el.offsetWidth; // Force reflow

      // Collapse to zero
      el.classList.add('is-dismissing');
      el.style.height = '0';
      el.style.paddingTop = '0';
      el.style.paddingBottom = '0';
      el.style.marginBottom = '0';

      setTimeout(() => {
        if (el.parentNode) {
          el.remove();
          this.cleanupEmptyContainers();
        }
      }, COLLAPSE_DURATION);
    }

    cleanupEmptyContainers() {
      const container = document.querySelector('[data-drupal-messages]');
      if (container && container.querySelectorAll('.status-message').length === 0) {
        container.innerHTML = '';
        container.style.display = 'none';
      }

      const fallback = document.querySelector('[data-drupal-messages-fallback]');
      if (fallback && fallback.children.length === 0) {
        fallback.innerHTML = '';
      }
    }

    destroy() {
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }
      this.progressBar = null;
    }
  }

  Drupal.behaviors.statusMessagesDismiss = {
    attach(context) {
      // Teleport the messages container to the top of #app to escape main's stacking context.
      once('teleport-messages', '[data-drupal-messages]', context).forEach(container => {
        const app = document.querySelector('#app') || document.body;
        if (container.parentElement !== app) {
          app.prepend(container);
        }
      });

      const messages = once('status-messages-controller', '.status-message', context);
      messages.forEach(el => {
        el._statusMessageController = new StatusMessageController(el);
      });
    },

    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;

      const elements = once.filter('status-messages-controller', '.status-message', context);
      elements.forEach(el => {
        if (el._statusMessageController) {
          el._statusMessageController.destroy();
          delete el._statusMessageController;
        }
      });

      once.remove('status-messages-controller', elements);
    }
  };

})(Drupal, once);
